import { useState } from 'react';
import type { ProgressStatusValue } from '@teentack/shared';
import { Badge, type BadgeTone } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { SelectInput } from '@/components/ui/Input';
import { ErrorNote } from '@/components/Form';
import { MapPinIcon } from '@/components/ui/Icons';
import { relativeTime } from '@/lib/relative-time';
import { presentApiError } from '@/lib/form-errors';
import {
  useAddProgress,
  useLocation,
  useProgress,
  useShareLocation,
  useStopSharingLocation,
} from './use-worksite';

/**
 * What is happening on a job. [Teen steps 15/17, Parent 14-16, Job Poster 23-24]
 *
 * One panel used by all three roles, because they are looking at the same
 * thing from different sides. The teen gets the controls; the guardian and the
 * employer get the same timeline, read-only.
 */

const PROGRESS_TONES: Record<ProgressStatusValue, BadgeTone> = {
  IN_PROGRESS: 'brand',
  BLOCKED: 'sun',
  COMPLETED: 'safe',
};

function label(value: string): string {
  return value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ');
}

/** The teen's location controls. Sharing is always their decision. */
function LocationControls({ assignmentId }: { assignmentId: string }) {
  const location = useLocation(assignmentId);
  const share = useShareLocation(assignmentId);
  const stop = useStopSharingLocation(assignmentId);
  const [error, setError] = useState<string | null>(null);

  const sharing = location.data?.sharing ?? false;

  function ping(status: 'EN_ROUTE' | 'ARRIVED' | 'DEPARTED'): void {
    setError(null);

    if (!('geolocation' in navigator)) {
      setError('This browser cannot share a location.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        share.mutate({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          ...(Number.isFinite(position.coords.accuracy)
            ? { accuracyM: Math.round(position.coords.accuracy) }
            : {}),
          status,
        });
      },
      // The browser's own message is unhelpful; say what to do instead.
      () => setError('Location is blocked. Allow it for this site, then try again.'),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  return (
    <div className="rounded-xl border border-line bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-ink">Share where you are</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {sharing
              ? `Your guardian and the employer can see this. Updated ${relativeTime(
                  location.data?.updatedAt ?? '',
                )}.`
              : 'Optional. Only your guardian and this employer would see it, and only during this job.'}
          </p>
        </div>
        {sharing && <Badge tone="safe">Sharing</Badge>}
      </div>

      {error && (
        <div className="mt-3">
          <ErrorNote message={error} />
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" disabled={share.isPending} onClick={() => ping('EN_ROUTE')}>
          On my way
        </Button>
        <Button variant="outline" disabled={share.isPending} onClick={() => ping('ARRIVED')}>
          I have arrived
        </Button>
        <Button variant="outline" disabled={share.isPending} onClick={() => ping('DEPARTED')}>
          I have left
        </Button>
        {sharing && (
          <Button variant="ghost" disabled={stop.isPending} onClick={() => stop.mutate()}>
            {stop.isPending ? 'Stopping…' : 'Stop sharing'}
          </Button>
        )}
      </div>
    </div>
  );
}

/** What a guardian or employer sees. Read-only by design. */
function LocationView({ assignmentId }: { assignmentId: string }) {
  const location = useLocation(assignmentId, true);
  const data = location.data;

  if (!data || !data.sharing) {
    return (
      <div className="rounded-xl border border-line bg-white p-4">
        <p className="inline-flex items-center gap-2 text-sm text-slate-500">
          <MapPinIcon className="size-4 text-slate-400" />
          Not sharing a location right now.
        </p>
        {data && data.trail.length > 0 && (
          <p className="mt-1 text-xs text-slate-400">
            Last seen {label(data.trail[0]!.status)} {relativeTime(data.trail[0]!.recordedAt)}.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-safe-200 bg-safe-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="inline-flex items-center gap-2 text-sm font-bold text-safe-800">
          <MapPinIcon className="size-4" />
          {label(data.status)}
        </p>
        <span className="text-xs text-safe-700">
          updated {relativeTime(data.updatedAt ?? '')}
        </span>
      </div>
      <p className="mt-1 text-xs text-safe-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {data.latitude?.toFixed(5)}, {data.longitude?.toFixed(5)}
        {data.accuracyM !== null && ` · within ${data.accuracyM}m`}
      </p>
      <a
        href={`https://www.openstreetmap.org/?mlat=${data.latitude}&mlon=${data.longitude}#map=17/${data.latitude}/${data.longitude}`}
        target="_blank"
        rel="noreferrer noopener"
        className="mt-2 inline-block text-xs font-semibold text-safe-800 underline"
      >
        Open in a map
      </a>
    </div>
  );
}

export function WorksitePanel({
  assignmentId,
  canPost,
}: {
  assignmentId: string;
  /** True for the teen doing the work — everyone else reads. */
  canPost: boolean;
}) {
  const progress = useProgress(assignmentId);
  const add = useAddProgress(assignmentId);

  const [milestone, setMilestone] = useState('');
  const [status, setStatus] = useState<ProgressStatusValue>('IN_PROGRESS');
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const text = milestone.trim();
    if (!text) return;

    setError(null);
    try {
      await add.mutateAsync({ milestone: text, status });
      setMilestone('');
      setStatus('IN_PROGRESS');
    } catch (caught) {
      setError(presentApiError(caught, 'Could not post that update').message);
    }
  }

  const items = progress.data ?? [];

  return (
    <div className="space-y-4">
      {canPost ? <LocationControls assignmentId={assignmentId} /> : <LocationView assignmentId={assignmentId} />}

      {canPost && (
        <form onSubmit={(event) => void submit(event)} className="rounded-xl border border-line bg-white p-4">
          <label htmlFor={`milestone-${assignmentId}`} className="text-sm font-bold text-ink">
            Post an update
          </label>
          <input
            id={`milestone-${assignmentId}`}
            value={milestone}
            onChange={(event) => setMilestone(event.target.value)}
            maxLength={150}
            placeholder="What did you just do?"
            className="mt-2 w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
          />

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="w-44">
              <SelectInput
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value as ProgressStatusValue)}
              >
                <option value="IN_PROGRESS">Getting on with it</option>
                <option value="BLOCKED">Stuck on something</option>
                <option value="COMPLETED">Finished this bit</option>
              </SelectInput>
            </div>
            <Button type="submit" disabled={add.isPending || milestone.trim().length === 0}>
              {add.isPending ? 'Posting…' : 'Post update'}
            </Button>
          </div>

          {error && (
            <div className="mt-3">
              <ErrorNote message={error} />
            </div>
          )}
        </form>
      )}

      {items.length > 0 && (
        <ol className="space-y-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-line bg-white px-4 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink">{item.milestone}</p>
                {item.notes && <p className="mt-0.5 text-xs text-slate-500">{item.notes}</p>}
                <p className="mt-1 text-[0.7rem] text-slate-400">
                  {relativeTime(item.updatedAt)}
                </p>
              </div>
              <Badge tone={PROGRESS_TONES[item.status]}>{label(item.status)}</Badge>
            </li>
          ))}
        </ol>
      )}

      {progress.data && items.length === 0 && !canPost && (
        <p className="rounded-xl border border-dashed border-line bg-white px-4 py-6 text-center text-sm text-slate-500">
          No updates yet.
        </p>
      )}
    </div>
  );
}
